import React, { createContext, useState, useEffect, useContext } from 'react';
import { PortalContext } from '@/config/navigation.config';

interface ShellContextType {
  isSidebarCollapsed: boolean;
  toggleSidebar: () => void;
  isMobileDrawerOpen: boolean;
  setIsMobileDrawerOpen: (open: boolean) => void;
  isCommandPaletteOpen: boolean;
  setIsCommandPaletteOpen: (open: boolean) => void;
  activePortal: PortalContext;
  setActivePortal: (portal: PortalContext) => void;
  isOnline: boolean;
}

const ShellContext = createContext<ShellContextType | undefined>(undefined);

export const ShellProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Sidebar Collapse State with localStorage persistence & safe fallback
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem('procureai_sidebar_collapsed');
      return stored ? JSON.parse(stored) === true : false;
    } catch {
      return false;
    }
  });

  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);
  const [activePortal, setActivePortalState] = useState<PortalContext>('MAIN');
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);

  useEffect(() => {
    try {
      localStorage.setItem('procureai_sidebar_collapsed', JSON.stringify(isSidebarCollapsed));
    } catch {
      // Ignore storage write error
    }
  }, [isSidebarCollapsed]);

  const toggleSidebar = () => {
    setIsSidebarCollapsed((prev) => !prev);
  };

  const setActivePortal = (portal: PortalContext) => {
    setActivePortalState(portal);
  };

  // Global Keyboard Shortcut: Ctrl + K or Cmd + K -> Command Palette
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Network Status Monitoring
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Body Scroll Locking when Mobile Drawer or Command Palette is open
  useEffect(() => {
    if (isMobileDrawerOpen || isCommandPaletteOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileDrawerOpen, isCommandPaletteOpen]);

  return (
    <ShellContext.Provider
      value={{
        isSidebarCollapsed,
        toggleSidebar,
        isMobileDrawerOpen,
        setIsMobileDrawerOpen,
        isCommandPaletteOpen,
        setIsCommandPaletteOpen,
        activePortal,
        setActivePortal,
        isOnline,
      }}
    >
      {children}
    </ShellContext.Provider>
  );
};

export function useShellState() {
  const context = useContext(ShellContext);
  if (!context) {
    throw new Error('useShellState must be used within a ShellProvider');
  }
  return context;
}
